export type CostCategory = 'material' | 'labor' | 'service' | 'other';
export type CostStatus = 'draft' | 'submitted' | 'approved' | 'rejected' | 'billed';
export type QuotationStatus = 'draft' | 'submitted' | 'approved' | 'rejected' | 'expired';
export type PaymentStatus = 'pending' | 'partial' | 'paid' | 'overdue' | 'cancelled';

export interface CostLineItem {
  item_id: string;
  description: string;
  quantity: number;
  unit: string;
  unit_price: number;
  amount: number;
  category: CostCategory;
}

export interface WorkOrderCost {
  cost_id: string;
  work_order_id: string;
  complaint_id: string;
  vendor_id: string;
  materials: CostLineItem[];
  labor: CostLineItem[];
  service: CostLineItem[];
  other: CostLineItem[];
  materials_subtotal: number;
  labor_subtotal: number;
  service_subtotal: number;
  other_subtotal: number;
  subtotal: number;
  tax_rate: number;
  tax_amount: number;
  total: number;
  currency: string;
  status: CostStatus;
  created_at: string;
  updated_at: string;
}

export interface Quotation {
  quotation_id: string;
  work_order_id: string;
  vendor_id: string;
  vendor_name: string;
  items: CostLineItem[];
  subtotal: number;
  tax_amount: number;
  total: number;
  validity_days: number;
  notes: string;
  status: QuotationStatus;
  submitted_at: string;
  approved_at?: string;
  approved_by?: string;
  rejection_reason?: string;
}

export interface Bill {
  bill_id: string;
  work_order_id: string;
  complaint_id: string;
  vendor_id: string;
  vendor_name: string;
  quotation_id: string;
  line_items: CostLineItem[];
  subtotal: number;
  tax_amount: number;
  total: number;
  bill_date: string;
  due_date: string;
  payment_status: PaymentStatus;
  payment_date?: string;
  payment_reference?: string;
}

export interface CreateCostDto {
  work_order_id: string;
  complaint_id: string;
  vendor_id: string;
  materials: CostLineItem[];
  labor: CostLineItem[];
  service: CostLineItem[];
  other: CostLineItem[];
  tax_rate: number;
}

export interface CreateQuotationDto {
  work_order_id: string;
  vendor_id: string;
  items: CostLineItem[];
  validity_days: number;
  notes: string;
}

export interface CreateBillDto {
  work_order_id: string;
  complaint_id: string;
  vendor_id: string;
  quotation_id: string;
  line_items: CostLineItem[];
  tax_rate: number;
  due_date: string;
}

export interface CostFilter {
  work_order_id?: string;
  complaint_id?: string;
  status?: CostStatus;
}

export interface QuotationFilter {
  work_order_id?: string;
  vendor_id?: string;
  status?: QuotationStatus;
}

export interface BillFilter {
  work_order_id?: string;
  vendor_id?: string;
  payment_status?: PaymentStatus;
}

export const COST_CATEGORY_LABELS: Record<CostCategory, string> = {
  material: 'Materials / Spare Parts',
  labor: 'Labor / Workmanship',
  service: 'Service Charges',
  other: 'Other Charges',
};

export const COST_CATEGORY_UNITS: CostCategory[] = ['material', 'labor', 'service', 'other'];

export const DEFAULT_TAX_RATE = 18;

export const PAYMENT_STATUS_COLORS: Record<PaymentStatus, string> = {
  pending: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  partial: 'bg-sky-500/10 text-sky-400 border-sky-500/20',
  paid: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
  overdue: 'bg-rose-500/10 text-rose-400 border-rose-500/20',
  cancelled: 'bg-slate-700/50 text-slate-400 border-slate-600/50',
};

export const QUOTATION_STATUS_COLORS: Record<QuotationStatus, string> = {
  draft: 'bg-slate-700/50 text-slate-400 border-slate-600/50',
  submitted: 'bg-sky-500/10 text-sky-400 border-sky-500/20',
  approved: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
  rejected: 'bg-rose-500/10 text-rose-400 border-rose-500/20',
  expired: 'bg-slate-700/50 text-slate-400 border-slate-600/50',
};
